/* eslint-disable react-hooks/exhaustive-deps */

import { useTimeTracker } from '@src/containers/App/TimeTracker/contexts/TimeTrackerProvider';
import { ReportContext } from '@src/containers/App/TimeTracker/Main/contexts/ReportContext';
import { getDeviceInfo } from '@src/packages/utils/functions';
import AttendanceRecordService from '@src/services/AttendanceRecordService';
import ReportService from '@src/services/ReportService';
import { notification } from 'antd';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { ABSENCE_REASONS } from '../../constants';
import {
  SHIFT_CHANGES_REQUEST_BY,
  ATTENDANCE_TYPES,
} from '@src/containers/App/TimeTracker/constants';
import { SHIFT_CHANGES_TYPES } from '../constants';

const useEditMarkDrawer = ({ Form, isOpen, setIsOpen, onClose }) => {
  const { organizationIds } = useTimeTracker();
  const profile = useSelector(({ user }) => user.profile);

  const intl = useIntl();
  const [formData, setFormData] = useState({
    requestedBy: SHIFT_CHANGES_REQUEST_BY.SUPERVISOR,
    correctionReason: '',
    newStartMarking: null,
    newEndMarking: null,
    newBreakStartMarking: null,
    newBreakEndMarking: null,
    absenceReason: null,
    isExcused: null,
    observations: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm();
  const {
    selectedReportRecord,
    setSelectedReportRecord,
    selectedAttendanceRecord,
    setSelectedAttendanceRecord,
  } = useContext(ReportContext);

  const handleClose = () => {
    setIsOpen(false);
    setSelectedReportRecord(null);
    setSelectedAttendanceRecord(null);
    onClose?.();
  };

  const handleChange = (key, value) => {
    if (
      (key === 'newStartMarking' ||
        key === 'newEndMarking' ||
        key === 'newBreakStartMarking' ||
        key === 'newBreakEndMarking') &&
      value !== null
    ) {
      value = value.format('YYYY-MM-DD HH:mm:ss');
    }

    if (key === 'newStartMarking' && formData.newEndMarking) {
      const startDate = moment(value);
      const endDate = moment(formData.newEndMarking);

      if (endDate.isBefore(startDate)) {
        setFormData({ ...formData, [key]: value, newEndMarking: null });
        return;
      }
    }

    if (key === 'newBreakStartMarking' && formData.newBreakEndMarking) {
      const startDate = moment(value);
      const endDate = moment(formData.newBreakEndMarking);

      if (endDate.isBefore(startDate)) {
        setFormData({ ...formData, [key]: value, newBreakEndMarking: null });
        return;
      }
    }

    setFormData({ ...formData, [key]: value });
  };

  const getObservations = values => {
    if (values.isExcused) {
      return (
        ABSENCE_REASONS.find(
          reason => reason.short === values.absenceReason || reason.code === values.absenceReason,
        )?.label || ''
      );
    }
    return values.observations || '';
  };

  const getAttendanceEventData = values => {
    const deviceInfo = getDeviceInfo();
    const actorId = profile?.data?.id || '';
    const name = profile?.data?.firstName || '';
    const lastName = profile?.data?.lastName || '';
    const actorFullname = `${name} ${lastName}`.trim();

    return {
      organizationId: organizationIds[0],
      employeeId: selectedAttendanceRecord?.employeeId || '',
      attendanceType: ATTENDANCE_TYPES.SHIFT_CHANGES,
      actorId: actorId,
      actorRole: SHIFT_CHANGES_REQUEST_BY.SUPERVISOR,
      actorFullname: actorFullname,
      attendanceRecordId: selectedReportRecord?.id || '',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      device: deviceInfo,
      location: null,
      shiftChanges: {
        actualStartAt: values.newStartMarking,
        actualEndAt: values.newEndMarking,
        actualBreakStartAt: values.newBreakStartMarking,
        actualBreakEndAt: values.newBreakEndMarking,
        observations: getObservations(values),
        leaveType: values.absenceReason || '',
        solicitationBy: values.requestedBy,
        changeType: values.correctionReason,
        isExcused: values.isExcused,
      },
    };
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const values = await form.validateFields();
      const data = getAttendanceEventData(values);
      const reportService = new ReportService();
      const res = await reportService.editWorkJourneyMark(data, organizationIds[0]);
      setIsLoading(false);
      handleClose();
      notification.success({
        message: (
          <div>
            {intl.formatMessage({ id: 'timeTracker.reports.editMark.success.line1' })}
            <br />
            {intl.formatMessage({ id: 'timeTracker.reports.editMark.success.line2' })}
          </div>
        ),
      });
    } catch (error) {
      console.error('Validation failed:', error);
      const errorCode = error?.code || '';
      const errorMessage = String(error?.message || '');
      const isPermissionDenied =
        errorCode.includes('permission-denied') ||
        errorMessage.toLowerCase().includes('permission-denied');
      const isShiftChangesConflict =
        errorCode.includes('failed-precondition') ||
        errorMessage.toLowerCase().includes('failed-precondition');

      const messageId = isPermissionDenied
        ? 'timeTracker.reports.editMark.error.permissionDenied'
        : isShiftChangesConflict
          ? 'timeTracker.reports.editMark.error.shiftChangesConflict'
          : 'timeTracker.reports.editMark.error';

      notification.error({
        message: intl.formatMessage({ id: messageId }),
      });
      setIsLoading(false);
    }
  };

  const addZeroToDate = date => {
    if (!date) return null;

    const separator = date.includes('/') ? '/' : '-';
    const parts = date.split(separator);
    if (parts.length !== 3 || parts.some(part => part.trim() === '')) {
      return null;
    }

    const newParts = parts.map(part => {
      if (part.length === 1) return `0${part}`;
      return part;
    });

    return newParts.join(separator);
  };

  /**
   *
   * @param {*} reportRecord
   * @returns {Object} - The formatted marking date { startMarking, endMarking }
   */
  const formatMarkingDate = reportRecord => {
    const date = reportRecord.date;
    const workingDayMarking = reportRecord.workingDayMarking;
    const workingDay = reportRecord.workingDay;
    let startMarking = null;
    let endMarking = null;

    if (typeof workingDayMarking === 'string' && workingDayMarking.includes('-')) {
      const markingParts = workingDayMarking.split('-');
      if (markingParts.length >= 2) {
        startMarking = markingParts[0].trim();
        endMarking = markingParts[1].trim();
      }
    } else if (typeof workingDay === 'string' && workingDay.includes('-')) {
      const markingParts = workingDay.split('-');
      if (markingParts.length >= 2) {
        startMarking = markingParts[0].trim();
        endMarking = markingParts[1].trim();
      }
    } else {
      startMarking = null;
      endMarking = null;
    }
    if (!date) return null;

    const parts = date.split('/');
    if (parts.length !== 3 || parts.some(part => part.trim() === '')) {
      return null;
    }
    const startMarkingDate = `${addZeroToDate(`${parts[2]}-${parts[1]}-${parts[0]}`)} ${startMarking}`;
    const endMarkingDate = `${addZeroToDate(`${parts[2]}-${parts[1]}-${parts[0]}`)} ${endMarking}`;
    const res = {
      startMarking: startMarking ? moment(startMarkingDate) : null,
      endMarking: endMarking ? moment(endMarkingDate) : null,
    };
    return res;
  };

  /**
   *
   * @param {*} reportRecord
   * @returns {Object} - The formatted break marking date { breakStartMarking, breakEndMarking }
   */
  const formatBreakMarkingDate = reportRecord => {
    const date = reportRecord.date;
    const markingQueue = reportRecord.markingQueue;
    const queue = reportRecord.queue;

    let breakStartMarking = null;
    let breakEndMarking = null;
    if (typeof markingQueue === 'string' && markingQueue.includes('-')) {
      const markingParts = markingQueue.split('-');
      if (markingParts.length >= 2) {
        breakStartMarking = markingParts[0].trim();
        breakEndMarking = markingParts[1].trim();
      }
    } else if (typeof queue === 'string' && queue.includes('-')) {
      const markingParts = queue.split('-');
      if (markingParts.length >= 2) {
        breakStartMarking = markingParts[0].trim();
        breakEndMarking = markingParts[1].trim();
      }
    } else {
      breakStartMarking = null;
      breakEndMarking = null;
    }
    if (!date) return null;

    const parts = date.split('/');
    if (parts.length !== 3 || parts.some(part => part.trim() === '')) {
      return null;
    }
    const breakStartMarkingDate = `${addZeroToDate(`${parts[2]}-${parts[1]}-${parts[0]}`)}  ${breakStartMarking}`;
    const breakEndMarkingDate = `${addZeroToDate(`${parts[2]}-${parts[1]}-${parts[0]}`)}  ${breakEndMarking}`;
    return {
      breakStartMarking: breakStartMarking ? moment(breakStartMarkingDate) : null,
      breakEndMarking: breakEndMarking ? moment(breakEndMarkingDate) : null,
    };
  };

  const disabledStartDate = (current, newStartMarking) => {
    if (!newStartMarking && !selectedReportRecord) return false;

    if (!newStartMarking && selectedReportRecord.date) {
      let defaultDate = addZeroToDate(selectedReportRecord.date);
      const parts = defaultDate.split('/');
      defaultDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
      defaultDate = `${defaultDate} 00:00:00`;
      newStartMarking = defaultDate;
    }

    const valueType = typeof newStartMarking === 'string';

    newStartMarking = valueType ? moment(newStartMarking) : newStartMarking;
    const oneDayBefore = newStartMarking.clone().subtract(1, 'day').startOf('day');
    const oneDayAfter = newStartMarking.clone().add(1, 'day').endOf('day');

    if (current < oneDayBefore || current > oneDayAfter) return true;

    return false;
  };

  const disabledEndTime = current => {
    if (!current || !formData.newStartMarking) return {};

    const startDate = moment(formData.newStartMarking);
    const currentDate = moment(current);

    // if is the same day, disable the hours and minutes before the start hour and minute
    if (currentDate.isSame(startDate, 'day')) {
      const startHour = startDate.hour();
      const startMinute = startDate.minute();

      return {
        disabledHours: () => {
          const hours = [];
          for (let i = 0; i < startHour; i++) {
            hours.push(i);
          }
          return hours;
        },
        disabledMinutes: selectedHour => {
          if (selectedHour === startHour) {
            const minutes = [];
            for (let i = 0; i < startMinute; i++) {
              minutes.push(i);
            }
            return minutes;
          }
          return [];
        },
      };
    }

    return {};
  };

  /**
   *
   * @param {*} current
   * @param {*} type 'start' | 'end'
   * @returns
   */
  const disabledBreakEndTime = (current, type = 'start') => {
    if (!current || !formData.newStartMarking) return {};

    let startDate;
    if (type === 'start') {
      startDate = moment(formData.newStartMarking);
    } else {
      startDate = moment(formData.newBreakStartMarking);
    }
    const currentDate = moment(current);

    // if is the same day, disable the hours and minutes before the start hour and minute
    if (currentDate.isSame(startDate, 'day')) {
      const startHour = startDate.hour();
      const startMinute = startDate.minute();

      return {
        disabledHours: () => {
          const hours = [];
          for (let i = 0; i < startHour; i++) {
            hours.push(i);
          }
          return hours;
        },
        disabledMinutes: selectedHour => {
          if (selectedHour === startHour) {
            const minutes = [];
            for (let i = 0; i < startMinute; i++) {
              minutes.push(i);
            }
            return minutes;
          }
          return [];
        },
      };
    }

    return {};
  };

  const buttonItems = [
    {
      iconName: 'save',
      action: handleSubmit,
      icon: <span className="material-icons-outlined">save</span>,
    },
  ];

  const defaultInitialValues = {
    requestedBy: SHIFT_CHANGES_REQUEST_BY.SUPERVISOR,
    correctionReason: SHIFT_CHANGES_TYPES.TIME_ADJUSTMENT,
    absenceReason: null,
    isExcused: null,
  };

  /**
   * @description Get the start hour and end hour from the working day marking
   * @param {*} selectedReportRecord
   * @returns {{startHour: string, endHour: string}} - The start hour and end hour
   */
  const getMarkingHours = selectedReportRecord => {
    const workingDayMarking = selectedReportRecord.workingDayMarking;
    const workingDay = selectedReportRecord.workingDay;
    let startMarking = null;
    let endMarking = null;
    if (typeof workingDayMarking === 'string' && workingDayMarking.includes('-')) {
      const markingParts = workingDayMarking.split('-');
      if (markingParts.length >= 2) {
        startMarking = markingParts[0].trim();
        endMarking = markingParts[1].trim();
      }
    } else if (typeof workingDay === 'string' && workingDay.includes('-')) {
      const markingParts = workingDay.split('-');
      if (markingParts.length >= 2) {
        startMarking = markingParts[0].trim();
        endMarking = markingParts[1].trim();
      }
    } else {
      startMarking = null;
      endMarking = null;
    }
    return {
      startHour: startMarking,
      endHour: endMarking,
    };
  };

  const initializeMarkingDate = (formatedMarkingDate, newFormData) => {
    const { startMarking, endMarking } = formatedMarkingDate;
    form.setFieldsValue(selectedReportRecord);
    form.setFieldsValue({
      newStartMarking: startMarking,
      newEndMarking: endMarking,
      ...defaultInitialValues,
    });
    newFormData = {
      ...newFormData,
      newStartMarking: startMarking,
      newEndMarking: endMarking,
    };
    return newFormData;
  };

  const initializeBreakMarkingDate = (formatedBreakMarkingDate, newFormData) => {
    const { breakStartMarking, breakEndMarking } = formatedBreakMarkingDate;
    form.setFieldsValue({
      newBreakStartMarking: breakStartMarking,
      newBreakEndMarking: breakEndMarking,
      ...defaultInitialValues,
    });
    newFormData = {
      ...newFormData,
      newBreakStartMarking: breakStartMarking,
      newBreakEndMarking: breakEndMarking,
    };
    return newFormData;
  };
  const initializeEmptyMarkingDate = newFormData => {
    const workingDay = addZeroToDate(selectedReportRecord.date).split('/');
    const workingDayDate = `${workingDay[2]}-${workingDay[1]}-${workingDay[0]}`;
    const { startHour, endHour } = getMarkingHours(selectedReportRecord);

    form.setFieldsValue({
      newStartMarking: moment(startHour ? workingDayDate + ' ' + startHour : workingDayDate),
      newEndMarking: moment(endHour ? workingDayDate + ' ' + endHour : workingDayDate),
      newBreakStartMarking: newFormData.newBreakStartMarking,
      newBreakEndMarking: newFormData.newBreakEndMarking,
      ...defaultInitialValues,
    });
    newFormData = {
      ...newFormData,
      newStartMarking: moment(startHour ? workingDayDate + ' ' + startHour : workingDayDate),
      newEndMarking: moment(endHour ? workingDayDate + ' ' + endHour : workingDayDate),
      newBreakStartMarking: newFormData.newBreakStartMarking,
      newBreakEndMarking: newFormData.newBreakEndMarking,
      newBreakEndMarking: moment(workingDayDate),
    };
    return newFormData;
  };

  useEffect(() => {
    let isMounted = true;

    const loadAttendanceRecord = async () => {
      const attendanceRecordId = selectedReportRecord?.attendanceRecordId;
      const organizationId = organizationIds?.[0];

      if (!isOpen || !attendanceRecordId || !organizationId) {
        setSelectedAttendanceRecord(null);
        return;
      }

      try {
        const attendanceRecordService = new AttendanceRecordService();
        const attendanceRecord = await attendanceRecordService.getAttendanceRecord({
          organizationId,
          attendanceRecordId,
        });

        if (isMounted) {
          setSelectedAttendanceRecord(attendanceRecord || null);
        }
      } catch (error) {
        console.error('Error loading attendance record:', error);
        if (isMounted) {
          setSelectedAttendanceRecord(null);
        }
      }
    };

    if (isOpen && selectedReportRecord) {
      loadAttendanceRecord();

      const formatedMarkingDate = formatMarkingDate(selectedReportRecord);
      const formatedBreakMarkingDate = formatBreakMarkingDate(selectedReportRecord);
      let newFormData = { ...formData };

      if (
        formatedMarkingDate &&
        formatedMarkingDate.startMarking &&
        formatedMarkingDate.endMarking
      ) {
        newFormData = initializeMarkingDate(formatedMarkingDate, newFormData);
      }

      if (
        formatedBreakMarkingDate &&
        formatedBreakMarkingDate.breakStartMarking &&
        formatedBreakMarkingDate.breakEndMarking
      ) {
        newFormData = initializeBreakMarkingDate(formatedBreakMarkingDate, newFormData);
      }

      // if the working day doesn't have mark
      if (!selectedReportRecord.workingDayMarking?.includes('-')) {
        newFormData = initializeEmptyMarkingDate(newFormData);
      } else {
        newFormData = initializeEmptyMarkingDate(newFormData);
      }

      newFormData = {
        ...newFormData,
        ...defaultInitialValues,
      };
      setFormData(newFormData);
    }

    return () => {
      isMounted = false;
    };
  }, [isOpen, selectedReportRecord, organizationIds, setSelectedAttendanceRecord]);
  return {
    form,
    formData,
    handleChange,
    handleSubmit,
    buttonItems,
    handleClose,
    isLoading,
    disabledStartDate,
    disabledEndTime,
    disabledBreakEndTime,
  };
};

export default useEditMarkDrawer;
